import { useEffect, useRef } from 'react';
import type { DocumentMeta } from '../../core/types';
import { getEngine } from '../../engine/engine';
import { MAX_ZOOM, MIN_ZOOM } from '../../gl/view';
import { actualPixels, fitToScreen, setZoom, zoomStep } from '../../state/actions';
import { getActiveDocument, getActiveView, getHistoryView, useAppStore } from '../../state/store';
import { Icon, NumberInput } from '../components/controls';
import { Panel } from './Panel';

function paintNavigatorPreview(canvas: HTMLCanvasElement, doc: DocumentMeta): void {
  const engine = getEngine();
  if (!engine.available) return;

  const image = engine.compositeThumbnail(doc, 200);
  if (!image) return;

  canvas.width = image.width;
  canvas.height = image.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const cell = 6;
  for (let y = 0; y < canvas.height; y += cell) {
    for (let x = 0; x < canvas.width; x += cell) {
      ctx.fillStyle = ((x / cell + y / cell) & 1) === 0 ? '#3a3a3d' : '#2e2e31';
      ctx.fillRect(x, y, cell, cell);
    }
  }
  ctx.putImageData(image, 0, 0);
}

export function NavigatorPanel() {
  const store = useAppStore();
  const open = store.panels.navigator;
  const doc = getActiveDocument(store);
  const view = getActiveView(store);
  const history = getHistoryView(store, store.activeDocumentId);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const revision = `${store.activeDocumentId}:${history.position}:${history.labels.length}:${doc?.width ?? 0}:${doc?.height ?? 0}:${store.pixelEpoch}`;

  useEffect(() => {
    if (!open || !doc) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    let cancelled = false;
    let raf = 0;

    const paint = (): void => {
      if (cancelled || !canvasRef.current) return;
      paintNavigatorPreview(canvasRef.current, doc);
    };

    // Defer one compositor frame — opening the panel races the main view's
    // releaseAll(), which left the first thumbnail empty until the next paint.
    paint();
    raf = requestAnimationFrame(() => {
      raf = requestAnimationFrame(paint);
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
  }, [open, doc, revision]);

  return (
    <Panel id="navigator" title="Navigator">
      <div className="panel__body navigator">
        {!doc && <p className="dialog__note">Open a document to navigate.</p>}
        {doc && (
          <>
            <canvas className="navigator__preview" ref={canvasRef} />
            <div className="navigator__zoom">
              <button
                type="button"
                className="button button--icon"
                title="Zoom out"
                onClick={() => zoomStep(-1)}
              >
                <Icon size={14} path="M6 12h12" />
              </button>
              <input
                className="slider"
                type="range"
                min={Math.log2(MIN_ZOOM) * 100}
                max={Math.log2(MAX_ZOOM) * 100}
                value={view ? Math.log2(view.zoom) * 100 : 0}
                disabled={!view}
                onChange={(event) => setZoom(2 ** (Number.parseFloat(event.target.value) / 100))}
              />
              <button
                type="button"
                className="button button--icon"
                title="Zoom in"
                onClick={() => zoomStep(1)}
              >
                <Icon size={14} path="M12 6v12M6 12h12" />
              </button>
            </div>
            <div className="field">
              <NumberInput
                value={view ? Math.round(view.zoom * 100) : 100}
                min={Math.round(MIN_ZOOM * 100)}
                max={Math.round(MAX_ZOOM * 100)}
                onChange={(value) => setZoom(value / 100)}
              />
              <span className="field__label">%</span>
              <button type="button" className="button" onClick={fitToScreen}>
                Fit
              </button>
              <button type="button" className="button" onClick={actualPixels}>
                1:1
              </button>
            </div>
          </>
        )}
      </div>
    </Panel>
  );
}
